import { Card, Typography } from 'antd';
import React from 'react';

import { RequestActions } from './RequestActions';
import { StatusIndicators } from './StatusIndicators';
import { TimeCounters } from './TimeCounters';
import { MObywatelRequest } from './types';
import { getStatusTag } from './utils';

const { Title, Text } = Typography;

interface MObywatelRequestCardProps {
  request: MObywatelRequest;
  codeInputs: { [key: string]: string };
  setCodeInputs: (inputs: { [key: string]: string }) => void;
  actionLoading: { [key: string]: boolean };
  onCodeSubmit: (requestId: string) => void;
  onConfirmVerification: (requestId: string, accepted: boolean) => void;
  onShowUserModal: (requestId: string) => void;
  onResetVerification: (requestId: string) => void;
}

export const MObywatelRequestCard: React.FC<MObywatelRequestCardProps> = ({
  request,
  codeInputs,
  setCodeInputs,
  actionLoading,
  onCodeSubmit,
  onConfirmVerification,
  onShowUserModal,
  onResetVerification,
}) => {
  return (
    <Card className="w-full hover:shadow-md transition-shadow" bodyStyle={{ padding: '16px' }}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* User Info Section */}
        <div className="lg:col-span-1">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">
                {request.userData.firstName[0]}
                {request.userData.lastName[0]}
              </span>
            </div>
            <div>
              <Title level={5} className="!mb-0">
                {request.userData.firstName} {request.userData.lastName}
              </Title>
              <Text type="secondary" className="text-xs">
                {request.userData.email}
              </Text>
            </div>
          </div>
          <div className="space-y-2">
            <Text type="secondary" className="text-xs block">
              Utworzono: {request.createdAt?.toDate?.()?.toLocaleString() || 'N/A'}
            </Text>
            {getStatusTag(request.status)}
            <StatusIndicators request={request} />
          </div>
        </div>

        {/* Time Counters Section */}
        <div className="lg:col-span-1">
          <div className="space-y-2">
            <Text strong className="text-sm block">
              Timery:
            </Text>
            <TimeCounters request={request} />
            {!request.instructionsShownAt &&
              !request.codeRequestedAt &&
              !request.codeProvidedAt &&
              !request.modalShownAt &&
              !request.userResponseDeadline && (
                <Text type="secondary" className="text-xs">
                  Brak aktywnych timerów
                </Text>
              )}
          </div>
        </div>

        {/* Actions Section */}
        <div className="lg:col-span-1">
          <div className="flex flex-col space-y-2">
            <Text strong className="text-sm">
              Akcje:
            </Text>
            <div className="flex flex-wrap gap-2">
              <RequestActions
                request={request}
                codeInputs={codeInputs}
                setCodeInputs={setCodeInputs}
                actionLoading={actionLoading}
                onCodeSubmit={onCodeSubmit}
                onConfirmVerification={onConfirmVerification}
                onShowUserModal={onShowUserModal}
                onResetVerification={onResetVerification}
              />
              {![
                'waiting_for_code',
                'code_expired',
                'code_provided',
                'completed',
                'rejected',
                'awaiting_user_response',
                'user_inactive',
              ].includes(request.status) && (
                <Text type="secondary" className="text-xs">
                  Brak dostępnych akcji
                </Text>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
